/**
 * Daily docket data. The leading hand works through the API's /crew routes; the office reads dockets under RLS
 * and records paper and corrected dockets through /admin.
 */
import { adminBlob, adminFetch, crewFetch } from "../../lib/api";
import { localMidnight } from "../../lib/format";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};

export interface DocketDraft {
  crew: { crewMemberId: string | null; name: string; role: string | null; start: string; finish: string; breakMinutes: number }[];
  vehicles: number;
  equipment: { id: string; quantity: number }[];
  notes: string | null;
}
export interface DocketView {
  job: { reference: string; title: string; client: string | null } | null;
  date: string;
  site: string | null;
  crew: { name: string; start: string; finish: string; breaks: string; worked: string; charged: string; bands: string; notes: string }[];
  totals: { band: string; people: number; hours: string }[];
  vehicles: string;
  equipment: string[];
  notes: string | null;
}
export type DocketStatus = "awaiting_signature" | "signed" | "disputed" | "paper" | "corrected" | "cancelled";
export type DocketState = "not_started" | "sent" | "signed" | "disputed" | "paper" | "corrected";
export interface DocketSummary {
  id: string; number: string; status: DocketStatus; supersededBy: string | null; correctsId: string | null;
  signerName: string | null; signerCompany: string | null; signedAtLocal: string | null; disputeNote: string | null; signedVia: string | null;
  links: { id: string; recipientName: string; recipientEmail: string | null; recipientPhone: string | null; status: string; expiresAt: string; sentAt: string }[];
  view: DocketView;
}
export interface DocketConfigView {
  confirmStatement: string; vehiclesLabel: string; equipment: { id: string; label: string; unit: "each" | "hour" }[]; minimumHours: number; overtimeAfterHours: number;
}
export interface DocketPack { state: DocketState; config: DocketConfigView; prefill: DocketDraft; dockets: DocketSummary[]; canFill?: boolean }
export interface SignerInput {
  name: string; position: string; company: string; email: string | null; signatureTyped: string | null; signatureDrawn: string | null;
  confirmed: true; dispute: string | null; gps: { lat: number; lng: number; accuracyM: number | null } | null;
}
export interface SentLink { url: string; emailed: boolean; emailError: string | null; sms: { to: string; body: string } | null }

export const DOCKET_STATE_LABEL: Record<DocketState, string> = {
  not_started: "Not started",
  sent: "Sent",
  signed: "Signed",
  disputed: "Signed with dispute",
  paper: "Signed on paper",
  corrected: "Corrected",
};

/* ---------- leading hand ---------- */

export async function crewDocket(bookingId: string): Promise<DocketPack> {
  if (PREVIEW) return previewOnly();
  return crewFetch(`/shifts/${bookingId}/docket`);
}
export async function previewDocket(bookingId: string, draft: DocketDraft): Promise<DocketView> {
  if (PREVIEW) return previewOnly();
  return (await crewFetch<{ view: DocketView }>(`/shifts/${bookingId}/docket/preview`, { method: "POST", body: JSON.stringify(draft) })).view;
}
export async function signDocketOnSite(bookingId: string, draft: DocketDraft, signer: SignerInput) {
  if (PREVIEW) return previewOnly();
  return crewFetch<{ docket: { id: string; number: string; status: DocketStatus }; emailed: boolean }>(`/shifts/${bookingId}/docket/sign`, { method: "POST", body: JSON.stringify({ draft, signer }) }, { timeoutMs: 90_000 });
}
export async function sendDocket(bookingId: string, draft: DocketDraft, recipient: { name: string; email: string | null; phone: string | null }) {
  if (PREVIEW) return previewOnly();
  return crewFetch<SentLink & { docket: { id: string; number: string } }>(`/shifts/${bookingId}/docket/send`, { method: "POST", body: JSON.stringify({ draft, recipient }) });
}
export async function withdrawDocket(bookingId: string, docketId: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  await crewFetch(`/shifts/${bookingId}/docket/${docketId}/cancel`, { method: "POST", body: "{}" });
}

/* ---------- office ---------- */

export async function officeDocket(bookingId: string): Promise<DocketPack> {
  if (PREVIEW) return previewOnly();
  return adminFetch(`/shifts/${bookingId}/docket`);
}
export async function recordPaperDocket(bookingId: string, draft: DocketDraft, paper: { signerName: string; signerCompany: string; signedOn: string; disputeNote: string; photo: File }) {
  if (PREVIEW) return previewOnly();
  const form = new FormData();
  form.set("draft", JSON.stringify(draft));
  form.set("signerName", paper.signerName);
  form.set("signerCompany", paper.signerCompany);
  form.set("signedOn", paper.signedOn);
  if (paper.disputeNote) form.set("disputeNote", paper.disputeNote);
  form.set("photo", paper.photo);
  return adminFetch<{ docket: { number: string } }>(`/shifts/${bookingId}/docket/paper`, { method: "POST", body: form }, { timeoutMs: 120_000 });
}
export async function correctDocket(docketId: string, draft: DocketDraft, reason: string, issuerName: string) {
  if (PREVIEW) return previewOnly();
  return adminFetch<{ docket: { number: string } }>(`/dockets/${docketId}/correct`, { method: "POST", body: JSON.stringify({ draft, reason, issuerName }) }, { timeoutMs: 90_000 });
}
export async function resendDocket(docketId: string, recipient: { name: string; email: string | null; phone: string | null }): Promise<SentLink> {
  if (PREVIEW) return previewOnly();
  return adminFetch(`/dockets/${docketId}/send`, { method: "POST", body: JSON.stringify(recipient) });
}
export async function cancelDocketLink(linkId: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  await adminFetch(`/docket-links/${linkId}/cancel`, { method: "POST", body: "{}" });
}
export const docketPdf = (id: string) => adminBlob(`/dockets/${id}/pdf`, {}, { timeoutMs: 90_000 });
export const docketPhoto = (id: string) => adminBlob(`/dockets/${id}/photo`, {}, { timeoutMs: 60_000 });

/** The docket state of each shift on a job (newest docket in force per shift). */
export async function docketStates(jobId: string): Promise<Record<string, { state: DocketState; number: string | null; disputeNote: string | null; signer: string | null }>> {
  if (PREVIEW) return {};
  const { data, error } = await supabase.from("dockets").select("booking_id, number, status, superseded_by, dispute_note, signer_name, created_at").eq("job_id", jobId).order("created_at");
  if (error) throw error;
  const out: Record<string, { state: DocketState; number: string | null; disputeNote: string | null; signer: string | null }> = {};
  for (const d of data as { booking_id: string; number: string; status: DocketStatus; superseded_by: string | null; dispute_note: string | null; signer_name: string | null }[]) {
    if (d.status === "cancelled" || d.superseded_by) continue;
    out[d.booking_id] = { state: d.status === "awaiting_signature" ? "sent" : d.status, number: d.number, disputeNote: d.dispute_note, signer: d.signer_name };
  }
  return out;
}

/** Today: yesterday's shifts (not cancelled) with no signed docket. */
export async function unsignedYesterday(today: string, yesterday: string): Promise<{ bookingId: string; jobId: string | null; reference: string | null; title: string | null; startsAt: string; state: DocketState }[]> {
  if (PREVIEW) return [];
  const from = localMidnight(yesterday).toISOString();
  const to = localMidnight(today).toISOString();
  const { data, error } = await supabase
    .from("bookings")
    .select("id, starts_at, status, job_id, jobs(reference, title), dockets(status, superseded_by)")
    .gte("starts_at", from)
    .lt("starts_at", to)
    .neq("status", "cancelled")
    .order("starts_at");
  if (error) throw error;
  type Row = { id: string; starts_at: string; job_id: string | null; jobs: { reference: string; title: string } | null; dockets: { status: DocketStatus; superseded_by: string | null }[] };
  return (data as unknown as Row[])
    .map((b) => {
      const inForce = b.dockets.filter((d) => d.status !== "cancelled" && !d.superseded_by).at(-1);
      const state = (!inForce ? "not_started" : inForce.status === "awaiting_signature" ? "sent" : inForce.status) as DocketState;
      return { bookingId: b.id, jobId: b.job_id, reference: b.jobs?.reference ?? null, title: b.jobs?.title ?? null, startsAt: b.starts_at, state };
    })
    .filter((r) => r.state === "not_started" || r.state === "sent");
}
